import React, { createContext, useContext, useReducer, useEffect, useState } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { logError } from '../utils/errorHandler';
import { useCatalog } from './CatalogContext';
import { useAuth } from './AuthContext';
import { db } from '../firebase/config';

const WishlistContext = createContext();

const wishlistReducer = (state, action) => {
  switch (action.type) {
    case 'ADD_TO_WISHLIST':
      // Check if item already exists
      const exists = state.items.find(item => item.id === action.payload.id);
      if (exists) {
        return state;
      }
      return {
        ...state,
        items: [...state.items, action.payload]
      };

    case 'REMOVE_FROM_WISHLIST':
      return {
        ...state,
        items: state.items.filter(item => item.id !== action.payload)
      };

    case 'CLEAR_WISHLIST':
      return {
        ...state,
        items: []
      };

    case 'LOAD_WISHLIST':
      return {
        ...state,
        items: action.payload
      };

    default:
      return state;
  }
};

const initialState = {
  items: []
};

export const WishlistProvider = ({ children }) => {
  const [state, dispatch] = useReducer(wishlistReducer, initialState);
  const [loading, setLoading] = useState(false);
  const { currentUser } = useAuth();
  const { allProducts } = useCatalog();

  const mergeWishlistItems = (items) => {
    const map = new Map();
    items.forEach((item) => {
      if (item && item.id) {
        map.set(item.id, item);
      }
    });
    return Array.from(map.values());
  };

  // Load wishlist from Firestore when logged in, fallback to localStorage when logged out
  useEffect(() => {
    let isMounted = true;

    const loadWishlist = async () => {
      if (!currentUser) {
        try {
          const savedWishlist = localStorage.getItem('wishlist');
          if (savedWishlist) {
            const parsedWishlist = JSON.parse(savedWishlist);
            if (Array.isArray(parsedWishlist)) {
              dispatch({ type: 'LOAD_WISHLIST', payload: parsedWishlist });
            }
          }
        } catch (error) {
          logError(error, { action: 'loadWishlistLocal' });
          localStorage.removeItem('wishlist');
        }
        return;
      }

      setLoading(true);
      try {
        let localItems = [];
        try {
          const savedWishlist = localStorage.getItem('wishlist');
          if (savedWishlist) {
            const parsedWishlist = JSON.parse(savedWishlist);
            if (Array.isArray(parsedWishlist)) {
              localItems = parsedWishlist;
            }
          }
        } catch {
          localStorage.removeItem('wishlist');
        }

        const userRef = doc(db, 'users', currentUser.uid);
        const snapshot = await getDoc(userRef);
        const remoteItems = Array.isArray(snapshot.data()?.wishlist) ? snapshot.data().wishlist : [];
        const merged = mergeWishlistItems([...remoteItems, ...localItems]);

        if (isMounted) {
          dispatch({ type: 'LOAD_WISHLIST', payload: merged });
        }

        if (localItems.length > 0) {
          localStorage.removeItem('wishlist');
        }

        if (merged.length > 0 || remoteItems.length > 0 || localItems.length > 0) {
          await setDoc(userRef, { wishlist: merged }, { merge: true });
        }
      } catch (error) {
        logError(error, { action: 'loadWishlistFirestore', userId: currentUser.uid });
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadWishlist();
    return () => {
      isMounted = false;
    };
  }, [currentUser]);

  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const shareFlag = params.get('wishlist');
      const idsParam = params.get('ids');
      if (shareFlag && idsParam) {
        const ids = idsParam.split(',').map(id => String(id).trim()).filter(Boolean);
        const sharedItems = allProducts.filter(p => ids.includes(String(p.id)));
        if (sharedItems.length > 0) {
          sharedItems.forEach(item => {
            dispatch({ type: 'ADD_TO_WISHLIST', payload: item });
          });
        }
      }
    } catch (error) {
      logError(error, { action: 'loadWishlistShare' });
    }
  }, [allProducts]);

  // Save wishlist to Firestore when logged in, localStorage when logged out
  useEffect(() => {
    const persistWishlist = async () => {
      try {
        if (currentUser) {
          const userRef = doc(db, 'users', currentUser.uid);
          await setDoc(userRef, { wishlist: state.items }, { merge: true });
        } else {
          localStorage.setItem('wishlist', JSON.stringify(state.items));
        }
      } catch (error) {
        logError(error, { action: 'saveWishlist', userId: currentUser?.uid });
      }
    };

    persistWishlist();
  }, [state.items, currentUser]);

  const addToWishlist = (product) => {
    try {
      setLoading(true);
      
      if (!product || !product.id) {
        throw new Error('Invalid product data');
      }
      
      dispatch({ type: 'ADD_TO_WISHLIST', payload: product });
      return { success: true, message: 'Added to wishlist' };
    } catch (error) {
      logError(error, { action: 'addToWishlist', productId: product?.id });
      return { success: false, message: error.message };
    } finally {
      setLoading(false);
    }
  };

  const removeFromWishlist = (productId) => {
    try {
      dispatch({ type: 'REMOVE_FROM_WISHLIST', payload: productId });
      return { success: true, message: 'Removed from wishlist' };
    } catch (error) {
      logError(error, { action: 'removeFromWishlist', productId });
      return { success: false, message: error.message };
    }
  };

  const clearWishlist = () => {
    try {
      dispatch({ type: 'CLEAR_WISHLIST' });
      return { success: true, message: 'Wishlist cleared' };
    } catch (error) {
      logError(error, { action: 'clearWishlist' });
      return { success: false, message: error.message };
    }
  };

  const isInWishlist = (productId) => {
    return state.items.some(item => item.id === productId);
  };

  const getWishlistCount = () => {
    return state.items.length;
  };

  const toggleWishlist = (product) => {
    if (isInWishlist(product.id)) {
      return removeFromWishlist(product.id);
    } else {
      return addToWishlist(product);
    }
  };

  const getShareUrl = () => {
    const ids = state.items.map(item => item.id).join(',');
    if (!ids) return '';
    return `${window.location.origin}/shop?wishlist=share&ids=${ids}`;
  };

  return (
    <WishlistContext.Provider value={{
      wishlist: state.items,
      loading,
      addToWishlist,
      removeFromWishlist,
      clearWishlist,
      isInWishlist,
      getWishlistCount,
      toggleWishlist,
      getShareUrl
    }}>
      {children}
    </WishlistContext.Provider>
  );
};

export const useWishlist = () => {
  const context = useContext(WishlistContext);
  if (!context) {
    throw new Error('useWishlist must be used within WishlistProvider');
  }
  return context;
};
